import React, { useState } from 'react';
import {
    Box,
    Container,
    Typography,
    TextField,
    Button,
    Paper,
    Grid,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    CircularProgress,
    Alert,
} from '@mui/material';

interface MLResponse {
    result: any;
    error?: string;
}

const MLTools: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<any>(null);
    const [tool, setTool] = useState('sentiment');
    const [input, setInput] = useState({
        text: '',
        context: '',
        topic: '',
        difficulty: 'medium',
        count: 5,
    });

    const handleToolChange = (event: any) => {
        setTool(event.target.value);
        setResult(null);
        setError(null);
    };

    const handleInputChange = (event: any) => {
        const { name, value } = event.target;
        setInput(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async () => {
        setLoading(true);
        setError(null);
        setResult(null);

        try {
            let endpoint = '';
            let payload: any = {};

            switch (tool) {
                case 'sentiment':
                    endpoint = '/api/ml/analyze-sentiment';
                    payload = { text: input.text };
                    break;
                case 'generate':
                    endpoint = '/api/ml/generate-text';
                    payload = { prompt: input.text };
                    break;
                case 'qa':
                    endpoint = '/api/ml/answer-question';
                    payload = { question: input.text, context: input.context };
                    break;
                case 'summarize':
                    endpoint = '/api/ml/summarize';
                    payload = { text: input.text };
                    break;
                case 'classify':
                    endpoint = '/api/ml/classify';
                    payload = { text: input.text };
                    break;
                case 'questions':
                    endpoint = '/api/ml/generate-questions';
                    payload = {
                        topic: input.topic,
                        difficulty: input.difficulty,
                        count: input.count
                    };
                    break;
            }

            const response = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const data: MLResponse = await response.json();

            if (data.error) {
                setError(data.error);
            } else {
                setResult(data.result);
            }
        } catch (err) {
            setError('An error occurred while processing your request');
        } finally {
            setLoading(false);
        }
    };

    const renderInputFields = () => {
        switch (tool) {
            case 'qa':
                return (
                    <>
                        <TextField
                            fullWidth
                            label="Question"
                            name="text"
                            value={input.text}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={2}
                        />
                        <TextField
                            fullWidth
                            label="Context"
                            name="context"
                            value={input.context}
                            onChange={handleInputChange}
                            margin="normal"
                            multiline
                            rows={4}
                        />
                    </>
                );
            case 'questions':
                return (
                    <>
                        <TextField
                            fullWidth
                            label="Topic"
                            name="topic"
                            value={input.topic}
                            onChange={handleInputChange}
                            margin="normal"
                        />
                        <FormControl fullWidth margin="normal">
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                                name="difficulty"
                                value={input.difficulty}
                                onChange={handleInputChange}
                            >
                                <MenuItem value="easy">Easy</MenuItem>
                                <MenuItem value="medium">Medium</MenuItem>
                                <MenuItem value="hard">Hard</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField
                            fullWidth
                            label="Number of Questions"
                            name="count"
                            type="number"
                            value={input.count}
                            onChange={handleInputChange}
                            margin="normal"
                        />
                    </>
                );
            default:
                return (
                    <TextField
                        fullWidth
                        label="Text"
                        name="text"
                        value={input.text}
                        onChange={handleInputChange}
                        margin="normal"
                        multiline
                        rows={4}
                    />
                );
        }
    };

    return (
        <Container maxWidth="md">
            <Box sx={{ my: 4 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                    AI Learning Tools
                </Typography>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <FormControl fullWidth margin="normal">
                        <InputLabel>Select Tool</InputLabel>
                        <Select
                            value={tool}
                            onChange={handleToolChange}
                        >
                            <MenuItem value="sentiment">Sentiment Analysis</MenuItem>
                            <MenuItem value="generate">Text Generation</MenuItem>
                            <MenuItem value="qa">Question Answering</MenuItem>
                            <MenuItem value="summarize">Text Summarization</MenuItem>
                            <MenuItem value="classify">Text Classification</MenuItem>
                            <MenuItem value="questions">Generate Practice Questions</MenuItem>
                        </Select>
                    </FormControl>

                    {renderInputFields()}

                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={loading}
                        sx={{ mt: 2 }}
                    >
                        {loading ? <CircularProgress size={24} /> : 'Process'}
                    </Button>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                {result && (
                    <Paper sx={{ p: 3 }}>
                        <Typography variant="h6" gutterBottom>
                            Result:
                        </Typography>
                        <Typography component="pre" sx={{ whiteSpace: 'pre-wrap' }}>
                            {JSON.stringify(result, null, 2)}
                        </Typography>
                    </Paper>
                )}
            </Box>
        </Container>
    );
};

export default MLTools; 